import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3,
  TrendingDown,
  AlertTriangle,
  Eye,
  CheckCircle2,
  Pencil,
  Inbox,
  Plus,
  FileText,
  ChevronDown,
  ChevronUp,
  Repeat,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WINDOW_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

function StatusDot({ status }) {
  const cls = status === "draft" ? "bg-amber-400" : "bg-emerald-500";
  const label = status === "draft" ? "Draft" : "Live";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium text-slate-500`}
      title={label}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${cls}`} />
      {label}
    </span>
  );
}

function LessonRow({ row, metric, onEdit }) {
  const primary =
    metric === "views"
      ? { label: "views", value: row.views }
      : metric === "completion"
      ? { label: "complete", value: `${row.completion_rate}%` }
      : { label: "demand", value: row.demand_score };

  return (
    <div
      data-testid={`insight-row-${row.lesson_id}`}
      className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg hover:bg-slate-50 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-sm font-medium text-slate-900 truncate">{row.title || "Untitled"}</p>
          <StatusDot status={row.status} />
        </div>
        <p className="text-[11px] text-slate-400 truncate">
          {row.section_title ? `${row.section_title} · ` : ""}
          {row.module_title || "—"}
          {row.linked_skills?.length ? (
            <span className="ml-2 text-blue-500">
              · {row.linked_skills.slice(0, 2).join(", ")}
              {row.linked_skills.length > 2 ? "…" : ""}
            </span>
          ) : null}
        </p>
      </div>
      <div className="text-right min-w-[60px]">
        <p className="text-sm font-bold text-slate-900 leading-none">{primary.value}</p>
        <p className="text-[10px] text-slate-400 uppercase tracking-wide mt-0.5">{primary.label}</p>
      </div>
      <button
        type="button"
        onClick={() => onEdit(row.lesson_id)}
        className="text-slate-400 hover:text-blue-600 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
        title="Open in CMS editor"
        data-testid={`insight-edit-${row.lesson_id}`}
      >
        <Pencil className="w-4 h-4" />
      </button>
    </div>
  );
}

function ListPanel({ title, icon: Icon, tint, rows, metric, emptyText, onEdit }) {
  return (
    <div className="bg-slate-50 rounded-xl p-4" data-testid={`insight-list-${metric}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${tint}`}>
          <Icon className="w-4 h-4" />
        </div>
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        <span className="ml-auto text-[10px] text-slate-400">{rows?.length || 0}</span>
      </div>
      {!rows || rows.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-slate-400 py-3">
          <Inbox className="w-3.5 h-3.5" />
          {emptyText}
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((r) => (
            <LessonRow key={r.lesson_id} row={r} metric={metric} onEdit={onEdit} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ContentInsightsPanel({ onEditLesson, onCreateLessonForSkill }) {
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDrafts, setShowDrafts] = useState(false);

  const fetchData = useCallback(async (days) => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/content-insights?days=${days}`,
        { withCredentials: true },
      );
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load content insights");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(windowDays);
  }, [fetchData, windowDays]);

  const handleEdit = (lessonId) => {
    if (typeof onEditLesson === "function") onEditLesson(lessonId);
  };

  const handleCreateForSkill = (skill) => {
    if (typeof onCreateLessonForSkill === "function") onCreateLessonForSkill(skill);
  };

  const totals = data?.totals || {};

  return (
    <div
      data-testid="admin-content-insights"
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
    >
      <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
        <div className="w-9 h-9 bg-indigo-100 rounded-xl flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-indigo-600" />
        </div>
        <div className="flex-1 min-w-[160px]">
          <h2 className="font-bold text-slate-900 leading-tight">Content Insight</h2>
          <p className="text-[11px] text-slate-400">
            What's working, what's stalling, and where learner demand is unmet.
          </p>
        </div>
        <div
          className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5"
          data-testid="insights-window-toggle"
        >
          {WINDOW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setWindowDays(opt.value)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                windowDays === opt.value
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`insights-window-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4">
        {/* Draft lessons awaiting review — closes the demand → draft → publish loop */}
        {data?.pending_review_drafts?.length > 0 && (
          <div
            data-testid="drafts-awaiting-review"
            className="mb-4 rounded-xl border border-amber-200 bg-amber-50"
          >
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-900">
                  Draft lessons awaiting review:{" "}
                  <span data-testid="drafts-awaiting-count">
                    {data.pending_review_drafts.length}
                  </span>
                </p>
                <p className="text-[11px] text-amber-700/80">
                  Demand-seeded drafts sitting unpublished — review and ship to close the loop.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowDrafts((v) => !v)}
                data-testid="review-drafts-btn"
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-600 text-white rounded-lg px-3 py-2 hover:bg-amber-700 active:scale-95 transition-all"
              >
                {showDrafts ? (
                  <>
                    Hide <ChevronUp className="w-3.5 h-3.5" />
                  </>
                ) : (
                  <>
                    Review drafts <ChevronDown className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </div>
            {showDrafts && (
              <div
                data-testid="drafts-awaiting-list"
                className="border-t border-amber-200 px-2 pt-1 pb-2"
              >
                {data.pending_review_drafts.map((r) => (
                  <LessonRow key={r.lesson_id} row={r} metric="demand" onEdit={handleEdit} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <div className="bg-indigo-50 rounded-xl p-3" data-testid="insights-total-views">
            <div className="flex items-center gap-2 mb-1.5">
              <Eye className="w-3.5 h-3.5 text-indigo-500" />
              <p className="text-[10px] text-indigo-500 uppercase tracking-wide font-semibold">Views</p>
            </div>
            <p className="text-2xl font-bold text-indigo-900 leading-none">
              {loading ? "—" : totals.total_views ?? 0}
            </p>
            <p className="text-[10px] text-indigo-400 mt-1">
              across {totals.total_lessons ?? 0} lessons
            </p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3" data-testid="insights-completion">
            <div className="flex items-center gap-2 mb-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              <p className="text-[10px] text-emerald-600 uppercase tracking-wide font-semibold">
                Avg Completion
              </p>
            </div>
            <p className="text-2xl font-bold text-emerald-900 leading-none">
              {loading ? "—" : `${totals.avg_completion_rate ?? 0}%`}
            </p>
            <p className="text-[10px] text-emerald-500 mt-1">
              {totals.total_completions ?? 0} finished
            </p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3" data-testid="insights-unmet-demand">
            <div className="flex items-center gap-2 mb-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
              <p className="text-[10px] text-amber-600 uppercase tracking-wide font-semibold">
                Unmet Demand
              </p>
            </div>
            <p className="text-2xl font-bold text-amber-900 leading-none">
              {loading ? "—" : totals.unmet_demand_skills ?? 0}
            </p>
            <p className="text-[10px] text-amber-500 mt-1">skills with no lesson</p>
          </div>
          <div className="bg-slate-100 rounded-xl p-3" data-testid="insights-loops-closed">
            <div className="flex items-center gap-2 mb-1.5">
              <Repeat className="w-3.5 h-3.5 text-slate-500" />
              <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">
                Loops Closed
              </p>
            </div>
            <p className="text-2xl font-bold text-slate-800 leading-none">
              {loading ? "—" : totals.loops_closed_this_month ?? 0}
            </p>
            <p className="text-[10px] text-slate-400 mt-1">
              this month
              {totals.loops_closed_all_time > (totals.loops_closed_this_month ?? 0) && (
                <> · {totals.loops_closed_all_time} all-time</>
              )}
            </p>
          </div>
        </div>

        {/* Three analytical lists */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <ListPanel
            title="Top viewed"
            icon={Eye}
            tint="bg-indigo-100 text-indigo-600"
            rows={data?.top_viewed || []}
            metric="views"
            emptyText="No lesson views yet in this window"
            onEdit={handleEdit}
          />
          <ListPanel
            title="Low completion"
            icon={TrendingDown}
            tint="bg-rose-100 text-rose-600"
            rows={data?.low_completion || []}
            metric="completion"
            emptyText="Nothing stalling — learners are finishing what they start"
            onEdit={handleEdit}
          />
          <ListPanel
            title="Underserved demand"
            icon={AlertTriangle}
            tint="bg-amber-100 text-amber-600"
            rows={data?.underserved_demand || []}
            metric="demand"
            emptyText="Demand is being met by current content"
            onEdit={handleEdit}
          />
        </div>

        {/* Unmet demand skill chips */}
        {data?.unmet_demand_skills?.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-[11px] text-slate-400 uppercase tracking-wide font-semibold">
                Skills with demand but no lesson
              </p>
              {typeof onCreateLessonForSkill === "function" && (
                <span className="text-[10px] text-amber-600 font-medium">· Tap to draft a lesson</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="unmet-demand-skills">
              {data.unmet_demand_skills.map((s) => {
                const canCreate = typeof onCreateLessonForSkill === "function";
                const inner = (
                  <>
                    {canCreate && <Plus className="w-3 h-3 text-amber-600" />}
                    {s.skill}
                    <span className="text-amber-500 font-bold">·{s.demand}</span>
                  </>
                );
                return canCreate ? (
                  <button
                    key={s.skill}
                    type="button"
                    onClick={() => handleCreateForSkill(s.skill)}
                    data-testid={`unmet-skill-create-${s.skill}`}
                    className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2.5 py-1 hover:bg-amber-100 hover:border-amber-300 active:scale-95 transition-all cursor-pointer"
                    title={`Draft a lesson for ${s.skill}`}
                  >
                    {inner}
                  </button>
                ) : (
                  <span
                    key={s.skill}
                    className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-800 border border-amber-100 rounded-full px-2.5 py-1"
                  >
                    {inner}
                  </span>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">
              {typeof onCreateLessonForSkill === "function"
                ? "Tap any skill to open a pre-tagged draft lesson — closes the learner demand → content loop in one click."
                : "Consider creating a lesson tagged with these skills to serve live coaching needs."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
